import React, { useState } from 'react';
import _get from 'lodash/get';
import _set from 'lodash/set';
import _cloneDeep from 'lodash/cloneDeep';
import _keyBy from 'lodash/keyBy';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from 'styles/ThemeTyping';
import { Field } from '@sift/types';
import * as Types from 'pages/Features/typings';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import DialogSelectItem from './molecules/DialogSelectItem';
import DefaultOrgChartAttrSettingsDialog from './DefaultOrgChartAttrSettingsDialog';
import ModernOrgChartCardImage from 'components/Icons/ModernOrgChartCardImage.png';
import ClassicAdvancedOrgChartCardImage from 'components/Icons/ClassicAdvancedOrgChartCardImage.png';
import ClassicSimpleOrgChartCardImage from 'components/Icons/ClassicSimpleOrgChartCardImage.png';

interface Props {
  fields: Field[];
  values: Types.FeatureValues;
  userStatus: number;
  onChange: (field: string, value: any) => void;
}

function DefaultOrgChartAttrSettings({ fields, values, userStatus, onChange }: Props) {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const cardViewValue = _get(values, ['person', 'directory', 'orgChart', 'view']);
  const cardStyleValue = _get(values, ['person', 'directory', 'orgChart', 'cardStyle'], 'modern');

  const handleViewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange('person.directory.orgChart.view', value);
  };

  const handleStyleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    onChange('person.directory.orgChart.cardStyle', value);
  };

  const handleConfigureDefaultInfo = (type: string) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  return (
    <>
      <Card className={classes.root}>
        <CardHeader
          className={classes.cardHeader}
          title="View and Card Content Defaults"
          subheader="Set the default org chart view and card content for new users."
        />
        <Divider className={classes.divider} />

        <div className={classes.section}>
          <Typography variant="h6">Default View</Typography>
          <Typography variant="subtitle2">
            Select which org chart view your users will see by default.
          </Typography>

          <RadioGroup value={cardViewValue} onChange={handleViewChange}>
            <FormControlLabel
              value="advanced"
              control={<Radio />}
              label={
                <>
                  <strong>Advanced View</strong> - A multi-level experience allowing for panning,
                  zooming, and expansion of multiple leaders simultaneously.
                </>
              }
            />
            <FormControlLabel
              value="simple"
              control={<Radio />}
              label={
                <>
                  <strong>Simple View</strong> - A single-level experience providing a more compact
                  view ideal for printing and simpler use cases.
                </>
              }
            />
          </RadioGroup>
        </div>

        <div className={classes.section}>
          <Typography variant="h6">Default Card Style</Typography>
          <Typography variant="subtitle2">
            Select which card style your users will see by default.
          </Typography>

          <RadioGroup value={cardStyleValue} onChange={handleStyleChange}>
            <FormControlLabel
              value="modern"
              control={<Radio />}
              label={
                <>
                  <strong>Extended</strong> - A flexible card that allows for multiple attributes to
                  be added or removed.
                </>
              }
            />
            <FormControlLabel
              value="classic"
              control={<Radio />}
              label={
                <>
                  <strong>Classic</strong> - A succinct card with predefined information.
                </>
              }
            />
          </RadioGroup>
        </div>

        <div className={classes.section}>
          <Typography variant="h6">Default Card Information</Typography>
          <Typography variant="subtitle2">
            For each card style, configure the default information that will appear.
          </Typography>

          <div className={classes.dialogSelectContainer}>
            <DialogSelectItem
              title="Extended Card (Advanced and Simple View)"
              imageSource={ModernOrgChartCardImage}
              className={classes.modernCardImage}
              onClick={() => handleConfigureDefaultInfo('modern')}
            />
            <DialogSelectItem
              title="Classic Card (Advanced View)"
              imageSource={ClassicAdvancedOrgChartCardImage}
              className={classes.classicAdvancedCardImage}
              onClick={() => handleConfigureDefaultInfo('classicAdvanced')}
            />
            <DialogSelectItem
              title="Classic Card (Simple View)"
              imageSource={ClassicSimpleOrgChartCardImage}
              className={classes.classicSimpleCardImage}
              onClick={() => handleConfigureDefaultInfo('classicSimple')}
            />
          </div>
        </div>
      </Card>
      <DefaultOrgChartAttrSettingsDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fields={fields}
        values={values}
        dialogType={dialogType}
        onChange={onChange}
      />
    </>
  );
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: 24,
  },
  cardHeader: {
    padding: 0,
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  dialogSelectContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  },
  modernCardImage: {
    height: 195,
    width: 194,
    marginBottom: theme.spacing(3.75),
  },
  classicAdvancedCardImage: {
    height: 197,
    width: 195,
    marginBottom: theme.spacing(3.75),
  },
  classicSimpleCardImage: {
    height: 151,
    width: 250,
    marginBottom: theme.spacing(9),
  },
}));

export default DefaultOrgChartAttrSettings;
